import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  MessageCircle, Send, Search, Users, CheckCircle2, Copy, XCircle, Loader2, Clock,
  RefreshCw, ChevronLeft, ChevronRight, Eye, Ban, Play,
} from "lucide-react";
import { toast } from "sonner";

const int = (n) => new Intl.NumberFormat("es-MX").format(n || 0);
const MAX_RECIPIENTS = 50000;

const CAMP_STATUS = {
  borrador: { label: "Borrador", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  programada: { label: "Programada", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  procesando: { label: "Procesando", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  finalizada: { label: "Finalizada", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  finalizada_errores: { label: "Finalizada c/errores", cls: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
  cancelada: { label: "Cancelada", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};
const PHONE_STATE = {
  valido: { label: "Válido", cls: "bg-emerald-500/15 text-emerald-300" },
  invalido: { label: "Inválido", cls: "bg-red-500/15 text-red-300" },
  sin_telefono: { label: "Sin teléfono", cls: "bg-slate-500/15 text-slate-400" },
};
const MSG_STATE = {
  pendiente: "text-slate-400", en_proceso: "text-amber-300", enviado: "text-emerald-300",
  error: "text-red-300", cancelado: "text-slate-500",
};

function Indicator({ label, value, tone = "slate", testid, loading }) {
  const tones = {
    slate: "text-white", emerald: "text-emerald-400", amber: "text-amber-400",
    red: "text-red-400", blue: "text-blue-400",
  };
  return (
    <div className="bg-[#131C2E] border border-[#223048] rounded-xl px-4 py-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className={`text-2xl font-extrabold tabular-nums mt-0.5 ${tones[tone]}`}>
        {loading ? <span className="text-slate-500 animate-pulse">…</span> : int(value)}
      </p>
    </div>
  );
}

export default function Campanias() {
  const qc = useQueryClient();
  const { structureKey, currentStructure, node } = useGlobal();
  const [tab, setTab] = useState("clientes");

  // ---- Clientes state ----
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [companyId, setCompanyId] = useState("all");
  const [categoria, setCategoria] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [sort, setSort] = useState("name asc");
  // selección: modo ids (explícito) o filter (todos los filtrados menos excluidos)
  const [sel, setSel] = useState({ mode: "ids", ids: new Set(), excluded: new Set() });
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const params = { structure: structureKey, node_id: node?.id };
  const reset = () => setSel({ mode: "ids", ids: new Set(), excluded: new Set() });
  useEffect(() => { reset(); setPage(1); }, [structureKey, node?.id, search, companyId, categoria]);

  const { data: cats } = useQuery({
    queryKey: ["camp-categorias"],
    queryFn: async () => (await api.get("/campanias/categorias")).data,
  });

  const { data: empresas } = useQuery({
    queryKey: ["camp-empresas", structureKey, node?.id],
    queryFn: async () => (await api.get("/campanias/empresas", { params })).data,
    enabled: !!structureKey,
  });
  const { data: clientes, isFetching, isError: clientesError } = useQuery({
    queryKey: ["camp-clientes", structureKey, node?.id, search, companyId, categoria, page, sort],
    queryFn: async () => (await api.get("/campanias/clientes", {
      params: { ...params, search: search || undefined, company_id: companyId === "all" ? undefined : companyId, categoria: categoria === "all" ? undefined : categoria, page, page_size: pageSize, sort },
    })).data,
    enabled: !!structureKey,
    keepPreviousData: true,
  });

  const rows = clientes?.rows || [];
  const total = clientes?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const selectedCount = sel.mode === "filter" ? Math.max(total - sel.excluded.size, 0) : sel.ids.size;
  const selectionPayload = useMemo(() => (
    sel.mode === "filter"
      ? { mode: "filter", search: search || undefined, company_id: companyId === "all" ? undefined : Number(companyId), categoria: categoria === "all" ? undefined : categoria, excluded_ids: [...sel.excluded] }
      : { mode: "ids", ids: [...sel.ids] }
  ), [sel, search, companyId, categoria]);

  const isRowSelected = (id) => sel.mode === "filter" ? !sel.excluded.has(id) : sel.ids.has(id);
  const toggleRow = (id) => setSel((s) => {
    if (s.mode === "filter") {
      const excluded = new Set(s.excluded);
      excluded.has(id) ? excluded.delete(id) : excluded.add(id);
      return { ...s, excluded };
    }
    const ids = new Set(s.ids);
    ids.has(id) ? ids.delete(id) : ids.add(id);
    return { ...s, ids };
  });
  const selectAllFiltered = () => {
    if (total > 1000 && !window.confirm(`Vas a seleccionar ${int(total)} clientes del filtro actual. ¿Continuar?`)) return;
    setSel({ mode: "filter", ids: new Set(), excluded: new Set() });
  };
  const clearSelection = () => reset();

  // indicadores dinámicos de la selección
  const { data: prev, isFetching: prevFetching } = useQuery({
    queryKey: ["camp-preview", structureKey, node?.id, JSON.stringify(selectionPayload)],
    queryFn: async () => (await api.post("/campanias/preview", { structure: structureKey, node_id: node?.id, selection: selectionPayload })).data,
    enabled: !!structureKey && selectedCount > 0,
    keepPreviousData: true,
  });

  return (
    <div className="space-y-6" data-testid="campanias-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-red-500 font-semibold">Campañas</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight flex items-center gap-2">
            <MessageCircle className="w-8 h-8 text-red-500" /> Campañas WhatsApp
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Clientes de {currentStructure?.name || "—"} · {node?.name || "Toda la estructura"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant={tab === "clientes" ? "default" : "outline"} onClick={() => setTab("clientes")} data-testid="tab-clientes" className={tab === "clientes" ? "bg-red-600 hover:bg-red-700 text-white" : "border-[#223048] text-slate-300"}>Clientes</Button>
          <Button variant={tab === "invalidos" ? "default" : "outline"} onClick={() => setTab("invalidos")} data-testid="tab-invalidos" className={tab === "invalidos" ? "bg-red-600 hover:bg-red-700 text-white" : "border-[#223048] text-slate-300"}>Clientes inválidos</Button>
          <Button variant={tab === "campanias" ? "default" : "outline"} onClick={() => setTab("campanias")} data-testid="tab-campanias" className={tab === "campanias" ? "bg-red-600 hover:bg-red-700 text-white" : "border-[#223048] text-slate-300"}>Campañas</Button>
        </div>
      </div>

      {(tab === "clientes" || tab === "invalidos") && (
        <div className="bg-[#131C2E] border border-[#223048] rounded-xl p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
          <form onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); }} className="relative flex-1">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Buscar cliente o teléfono…" data-testid="clientes-search" className="pl-9 bg-slate-950/60 border-slate-700 text-white" />
          </form>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="h-10 bg-slate-950/60 border-slate-700 text-white sm:w-56" data-testid="clientes-empresa"><SelectValue placeholder="Empresa" /></SelectTrigger>
            <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-h-72">
              <SelectItem value="all">Todas las empresas</SelectItem>
              {(empresas?.companies || []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoria} onValueChange={setCategoria}>
            <SelectTrigger className="h-10 bg-slate-950/60 border-slate-700 text-white sm:w-52" data-testid="clientes-categoria"><SelectValue placeholder="Categoría" /></SelectTrigger>
            <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-h-72">
              <SelectItem value="all">Todas las categorías</SelectItem>
              {(cats?.categorias || []).map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {tab === "clientes" && (
            <Button onClick={() => setCreateOpen(true)} disabled={selectedCount === 0} className="bg-red-600 hover:bg-red-700 text-white" data-testid="crear-campania-button">
              <Send className="w-4 h-4 mr-2" /> Crear campaña ({int(selectedCount)})
            </Button>
          )}
        </div>
      )}

      {tab === "clientes" && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Indicator label="Clientes encontrados" value={total} testid="ind-encontrados" />
            <Indicator label="Seleccionados" value={selectedCount} tone="blue" testid="ind-seleccionados" />
            <Indicator label="Números válidos" value={prev?.valid ?? 0} tone="emerald" testid="ind-validos" loading={prevFetching} />
            <Indicator label="Duplicados" value={prev?.duplicates_removed ?? 0} tone="amber" testid="ind-duplicados" loading={prevFetching} />
            <Indicator label="Inválidos" value={prev?.invalid ?? 0} tone="red" testid="ind-invalidos" loading={prevFetching} />
          </div>

          <div className="flex items-center justify-between text-xs text-slate-400 px-1">
            <div className="flex items-center gap-3">
              {sel.mode === "filter"
                ? <span className="text-blue-300">Seleccionados los {int(selectedCount)} clientes del filtro. <button onClick={clearSelection} className="underline" data-testid="clear-selection">Deseleccionar</button>{selectedCount > MAX_RECIPIENTS && <span className="block text-amber-400 mt-0.5" data-testid="max-warning">⚠ Se enviará a un máximo de {int(MAX_RECIPIENTS)} destinatarios (tope del sistema).</span>}</span>
                : <button onClick={selectAllFiltered} className="underline hover:text-white" data-testid="select-all-filtered">Seleccionar los {int(total)} del filtro</button>}
            </div>
            {isFetching && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>

          <div className="bg-[#131C2E] border border-[#223048] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#223048] hover:bg-transparent">
                    <TableHead className="w-10"><Checkbox checked={sel.mode === "filter" && sel.excluded.size === 0} onCheckedChange={(v) => v ? selectAllFiltered() : clearSelection()} className="border-slate-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" data-testid="select-all-check" /></TableHead>
                    <TableHead className="text-slate-400 cursor-pointer" onClick={() => setSort(sort === "name asc" ? "name desc" : "name asc")}>Cliente</TableHead>
                    <TableHead className="text-slate-400">Empresa</TableHead>
                    <TableHead className="text-slate-400">Teléfono</TableHead>
                    <TableHead className="text-slate-400">Celular</TableHead>
                    <TableHead className="text-slate-400">Normalizado</TableHead>
                    <TableHead className="text-slate-400">Correo</TableHead>
                    <TableHead className="text-slate-400">Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientesError && <TableRow><TableCell colSpan={8} className="text-center text-red-300 py-10" data-testid="clientes-error">No se pudieron cargar los clientes desde Odoo. Reintenta en unos segundos.</TableCell></TableRow>}
                  {!clientesError && rows.map((r) => {
                    const ps = PHONE_STATE[r.phone_estado] || PHONE_STATE.sin_telefono;
                    return (
                      <TableRow key={r.id} className="border-[#223048]" data-testid={`cliente-row-${r.id}`}>
                        <TableCell><Checkbox checked={isRowSelected(r.id)} onCheckedChange={() => toggleRow(r.id)} disabled={r.phone_estado !== "valido"} className="border-slate-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" data-testid={`cliente-check-${r.id}`} /></TableCell>
                        <TableCell className="text-white font-medium">{r.name}{r.vat ? <span className="block text-[11px] text-slate-500">{r.vat}</span> : null}</TableCell>
                        <TableCell className="text-slate-300">{r.company_name || "—"}{r.categoria && <span className="block text-[11px] text-slate-500">{r.categoria}</span>}</TableCell>
                        <TableCell className="text-slate-300 tabular-nums">{r.phone || "—"}</TableCell>
                        <TableCell className="text-slate-300 tabular-nums">{r.mobile || "—"}</TableCell>
                        <TableCell className="text-slate-200 tabular-nums">{r.phone_normalized || "—"}</TableCell>
                        <TableCell className="text-slate-400">{r.email || "—"}</TableCell>
                        <TableCell><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${ps.cls}`}>{ps.label}</span></TableCell>
                      </TableRow>
                    );
                  })}
                  {!clientesError && !rows.length && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">Sin clientes para los filtros actuales.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#223048] text-sm text-slate-400">
              <span>Página {page} de {totalPages} · {int(total)} clientes</span>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-[#223048] text-slate-300" data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="border-[#223048] text-slate-300" data-testid="page-next"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>

          <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} structure={structureKey} nodeId={node?.id}
            selection={selectionPayload} preview={prev} onCreated={() => { setCreateOpen(false); reset(); setTab("campanias"); qc.invalidateQueries({ queryKey: ["camp-list"] }); }} />
        </>
      )}
      {tab === "invalidos" && <InvalidosTab structureKey={structureKey} nodeId={node?.id} search={search} companyId={companyId} categoria={categoria} />}
      {tab === "campanias" && <CampaignsTab onOpen={setDetailId} />}

      <DetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function CreateDialog({ open, onClose, structure, nodeId, selection, preview, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", message: "", send_mode: "now", scheduled_at: "", use_template: true, content_sid: "", content_vars: {}, simulate: false });
  useEffect(() => { if (open) setForm({ name: "", description: "", message: "", send_mode: "now", scheduled_at: "", use_template: true, content_sid: "", content_vars: {}, simulate: false }); }, [open]);

  const { data: tplData } = useQuery({
    queryKey: ["camp-plantillas"],
    queryFn: async () => (await api.get("/campanias/plantillas")).data,
    enabled: open,
  });
  const templates = tplData?.templates || [];
  const selectedTpl = templates.find((t) => t.content_sid === form.content_sid) || null;
  useEffect(() => {
    if (open && templates.length && !form.content_sid) {
      const def = templates.find((t) => t.default) || templates[0];
      setForm((f) => ({ ...f, use_template: true, content_sid: def.content_sid, content_vars: {} }));
    }
  }, [open, templates]); // eslint-disable-line react-hooks/exhaustive-deps

  const createMut = useMutation({
    mutationFn: async () => {
      const payload = { name: form.name, description: form.description, message: form.message, structure, node_id: nodeId, selection, send_mode: form.send_mode, simulate: form.simulate };
      if (form.use_template && form.content_sid) {
        payload.content_sid = form.content_sid;
        const vars = {};
        (selectedTpl?.variables || []).forEach((v) => {
          const val = (form.content_vars[v.key] || "").trim();
          if (val) vars[v.key] = val;
        });
        if (Object.keys(vars).length) payload.content_variables = vars;
      }
      if (form.send_mode === "schedule" && form.scheduled_at) {
        payload.scheduled_at = new Date(form.scheduled_at).toISOString();
      }
      return (await api.post("/campanias", payload)).data;
    },
    onSuccess: (d) => { toast.success(d.status === "programada" ? "Campaña programada" : "Campaña creada, enviando…"); onCreated(); },
    onError: (e) => toast.error(e.response ? formatApiErrorDetail(e.response?.data?.detail) : e.message),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white font-display">Nueva campaña</DialogTitle>
          <DialogDescription className="text-slate-400">Configura el mensaje y la modalidad de envío.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-slate-300">Nombre</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campania-name" className="mt-1 bg-slate-950/60 border-slate-700 text-white" /></div>
          <div><Label className="text-slate-300">Descripción (opcional)</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="campania-desc" className="mt-1 bg-slate-950/60 border-slate-700 text-white" /></div>
          {!form.use_template && (
            <div><Label className="text-slate-300">Mensaje</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} data-testid="campania-message" className="mt-1 bg-slate-950/60 border-slate-700 text-white" placeholder="Escribe el mensaje de WhatsApp…" /></div>
          )}

          <div className="rounded-lg border border-[#223048] p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid="toggle-template">
              <Checkbox checked={form.use_template} onCheckedChange={() => setForm({ ...form, use_template: !form.use_template })} className="border-slate-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
              Usar plantilla aprobada de WhatsApp (recomendado para producción)
            </label>
            {form.use_template && (
              <div className="space-y-3 pl-6">
                <div><Label className="text-slate-300 text-xs">Plantilla</Label>
                  <Select value={form.content_sid} onValueChange={(v) => setForm({ ...form, content_sid: v, content_vars: {} })}>
                    <SelectTrigger className="mt-1 bg-slate-950/60 border-slate-700 text-white" data-testid="campania-template-select"><SelectValue placeholder="Selecciona una plantilla…" /></SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => <SelectItem key={t.content_sid} value={t.content_sid}>{t.label}{t.default ? " (por defecto)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {selectedTpl && (
                  <div className="rounded-md bg-slate-950/50 border border-[#223048] p-2 text-xs text-slate-300 whitespace-pre-line" data-testid="campania-template-preview">{selectedTpl.body}</div>
                )}
                {(selectedTpl?.variables || []).map((v) => (
                  <div key={v.key}><Label className="text-slate-300 text-xs">{v.label} {`{{${v.key}}}`}</Label>
                    <Input value={form.content_vars[v.key] || ""} onChange={(e) => setForm({ ...form, content_vars: { ...form.content_vars, [v.key]: e.target.value } })} data-testid={`campania-var-${v.key}`} className="mt-1 bg-slate-950/60 border-slate-700 text-white" placeholder={`Texto para ${v.label}`} /></div>
                ))}
                <p className="text-[11px] text-slate-500">Solo plantillas aprobadas por WhatsApp pueden enviarse masivamente. El texto que escribas reemplaza la variable del mensaje.</p>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button type="button" variant={form.send_mode === "now" ? "default" : "outline"} onClick={() => setForm({ ...form, send_mode: "now" })} className={form.send_mode === "now" ? "bg-red-600 text-white" : "border-[#223048] text-slate-300"} data-testid="mode-now">Enviar ahora</Button>
            <Button type="button" variant={form.send_mode === "schedule" ? "default" : "outline"} onClick={() => setForm({ ...form, send_mode: "schedule" })} className={form.send_mode === "schedule" ? "bg-red-600 text-white" : "border-[#223048] text-slate-300"} data-testid="mode-schedule">Programar</Button>
          </div>
          {form.send_mode === "schedule" && (
            <div><Label className="text-slate-300">Fecha y hora</Label>
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="campania-schedule" className="mt-1 bg-slate-950/60 border-slate-700 text-white" /></div>
          )}
          <div className="rounded-lg bg-slate-950/50 border border-[#223048] p-3 text-sm space-y-1" data-testid="campania-summary">
            <p className="flex justify-between"><span className="text-slate-400">Números válidos</span><span className="text-emerald-300 font-semibold tabular-nums">{int(preview?.valid ?? 0)}</span></p>
            <p className="flex justify-between"><span className="text-slate-400">Duplicados eliminados</span><span className="text-amber-300 font-semibold tabular-nums">{int(preview?.duplicates_removed ?? 0)}</span></p>
            <p className="flex justify-between"><span className="text-slate-400">Inválidos</span><span className="text-red-300 font-semibold tabular-nums">{int(preview?.invalid ?? 0)}</span></p>
            <p className="flex justify-between border-t border-[#223048] pt-1 mt-1"><span className="text-white font-semibold">Destinatarios finales</span><span className="text-white font-extrabold tabular-nums" data-testid="destinatarios-finales">{int(preview?.final ?? 0)}</span></p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => createMut.mutate()} disabled={createMut.isPending || !form.name.trim() || (!form.message.trim() && !(form.use_template && form.content_sid)) || (form.send_mode === "schedule" && !form.scheduled_at) || !(preview?.final > 0)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirmar-campania">
            {createMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar y generar cola"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const QUEUE_STATES = ["borrador", "programada", "procesando"];

function CampaignsTab({ onOpen }) {
  const [sub, setSub] = useState("cola");
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["camp-list"],
    queryFn: async () => (await api.get("/campanias")).data,
    refetchInterval: 5000,
  });
  const all = data?.campaigns || [];
  const cola = all.filter((c) => QUEUE_STATES.includes(c.status));
  const historial = all.filter((c) => !QUEUE_STATES.includes(c.status));
  const camps = sub === "cola" ? cola : historial;
  return (
    <div className="bg-[#131C2E] border border-[#223048] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#223048]">
        <div className="flex gap-1.5">
          <button onClick={() => setSub("cola")} data-testid="subtab-cola" className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${sub === "cola" ? "bg-red-600/20 text-red-300" : "text-slate-400 hover:bg-white/5"}`}>En cola ({int(cola.length)})</button>
          <button onClick={() => setSub("historial")} data-testid="subtab-historial" className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${sub === "historial" ? "bg-red-600/20 text-red-300" : "text-slate-400 hover:bg-white/5"}`}>Historial ({int(historial.length)})</button>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="border-[#223048] text-slate-300" data-testid="refresh-campanias"><RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /></Button>
      </div>
      {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#223048] hover:bg-transparent">
                <TableHead className="text-slate-400">Campaña</TableHead>
                <TableHead className="text-slate-400">Estado</TableHead>
                <TableHead className="text-slate-400 text-right">Destinatarios</TableHead>
                <TableHead className="text-slate-400 text-right">Enviados</TableHead>
                <TableHead className="text-slate-400 text-right">Errores</TableHead>
                <TableHead className="text-slate-400 text-right">Avance</TableHead>
                <TableHead className="text-slate-400">{sub === "cola" ? "Programada / Creada" : "Creada"}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {camps.map((c) => {
                const st = CAMP_STATUS[c.status] || CAMP_STATUS.borrador;
                return (
                  <TableRow key={c.id} className="border-[#223048]" data-testid={`campania-row-${c.id}`}>
                    <TableCell className="text-white font-medium">{c.name}<span className="block text-[11px] text-slate-500">{c.owner_name}</span></TableCell>
                    <TableCell><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${st.cls}`}>{st.label}</span></TableCell>
                    <TableCell className="text-right text-slate-200 tabular-nums">{int(c.recipients_count)}</TableCell>
                    <TableCell className="text-right text-emerald-300 tabular-nums">{int(c.enviados)}</TableCell>
                    <TableCell className="text-right text-red-300 tabular-nums">{int(c.errores)}</TableCell>
                    <TableCell className="text-right text-slate-200 tabular-nums">{c.avance}%</TableCell>
                    <TableCell className="text-slate-400 text-xs">{(sub === "cola" && c.scheduled_at) ? new Date(c.scheduled_at).toLocaleString("es-MX") : (c.created_at ? new Date(c.created_at).toLocaleString("es-MX") : "—")}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => onOpen(c.id)} className="text-slate-400 hover:text-red-400" data-testid={`ver-campania-${c.id}`}><Eye className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                );
              })}
              {!camps.length && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">{sub === "cola" ? "No hay campañas en cola ni programadas." : "Aún no hay campañas finalizadas."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function InvalidosTab({ structureKey, nodeId, search, companyId, categoria }) {
  const [page, setPage] = useState(1);
  const pageSize = 50;
  useEffect(() => { setPage(1); }, [structureKey, nodeId, search, companyId, categoria]);
  const { data, isFetching } = useQuery({
    queryKey: ["camp-invalidos", structureKey, nodeId, search, companyId, categoria, page],
    queryFn: async () => (await api.get("/campanias/clientes/invalidos", {
      params: { structure: structureKey, node_id: nodeId, search: search || undefined, company_id: companyId === "all" ? undefined : companyId, categoria: categoria === "all" ? undefined : categoria, page, page_size: pageSize },
    })).data,
    enabled: !!structureKey,
    keepPreviousData: true,
  });
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="space-y-3" data-testid="invalidos-tab">
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <XCircle className="w-4 h-4 text-red-400" />
        <span><b className="text-red-300">{int(total)}</b> clientes con teléfono pero con número inválido (no se pueden incluir en campañas).</span>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
      </div>
      {data?.capped && <p className="text-xs text-amber-400">El conteo se calcula sobre una muestra de hasta 5,000 clientes por filtro; acota por empresa o categoría para revisar todos los inválidos.</p>}
      <div className="bg-[#131C2E] border border-[#223048] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#223048] hover:bg-transparent">
                <TableHead className="text-slate-400">Cliente</TableHead>
                <TableHead className="text-slate-400">Empresa</TableHead>
                <TableHead className="text-slate-400">Teléfono</TableHead>
                <TableHead className="text-slate-400">Celular</TableHead>
                <TableHead className="text-slate-400">Correo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="border-[#223048]" data-testid={`invalido-row-${r.id}`}>
                  <TableCell className="text-white font-medium">{r.name}</TableCell>
                  <TableCell className="text-slate-300">{r.company_name || "—"}{r.categoria && <span className="block text-[11px] text-slate-500">{r.categoria}</span>}</TableCell>
                  <TableCell className="text-red-300 tabular-nums">{r.phone || "—"}</TableCell>
                  <TableCell className="text-red-300 tabular-nums">{r.mobile || "—"}</TableCell>
                  <TableCell className="text-slate-400">{r.email || "—"}</TableCell>
                </TableRow>
              ))}
              {!rows.length && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10">No hay números inválidos para los filtros actuales.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#223048] text-sm text-slate-400">
          <span>Página {page} de {totalPages} · {int(total)} inválidos</span>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-[#223048] text-slate-300" data-testid="inv-page-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="icon" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="border-[#223048] text-slate-300" data-testid="inv-page-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailDialog({ id, onClose }) {
  const qc = useQueryClient();
  const [onlyErrors, setOnlyErrors] = useState(false);
  const { data: camp } = useQuery({
    queryKey: ["camp-detail", id],
    queryFn: async () => (await api.get(`/campanias/${id}`)).data,
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.status === "procesando" ? 3000 : false),
  });
  const { data: recs } = useQuery({
    queryKey: ["camp-recs", id, onlyErrors],
    queryFn: async () => (await api.get(`/campanias/${id}/recipients`, { params: { only_errors: onlyErrors, page_size: 200 } })).data,
    enabled: !!id,
    refetchInterval: camp?.status === "procesando" ? 4000 : false,
  });
  const action = useMutation({
    mutationFn: async (act) => (await api.post(`/campanias/${id}/${act}`)).data,
    onSuccess: () => {
      toast.success("Acción aplicada");
      qc.invalidateQueries({ queryKey: ["camp-detail", id] });
      qc.invalidateQueries({ queryKey: ["camp-recs", id] });
      qc.invalidateQueries({ queryKey: ["camp-list"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const s = camp?.stats || {};
  const st = CAMP_STATUS[camp?.status] || CAMP_STATUS.borrador;

  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-white font-display flex items-center gap-2">
            {camp?.name || "Campaña"}
            {camp && <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${st.cls}`}>{st.label}</span>}
          </DialogTitle>
          <DialogDescription className="text-slate-400">{camp ? `${camp.description || "Sin descripción"} · ${camp.owner_name}` : "Cargando…"}</DialogDescription>
        </DialogHeader>
        {!camp ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div>
        ) : (
          <>
            <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
              {camp.status === "procesando" && (
                <div>
                  <div className="flex justify-between text-sm mb-1"><span className="text-amber-300">Procesando… {int(s.procesados)} / {int(s.total)}</span><span className="text-white font-semibold">{s.avance}%</span></div>
                  <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-red-600 transition-all" style={{ width: `${s.avance || 0}%` }} data-testid="progress-bar" /></div>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Indicator label="Destinatarios" value={s.total} testid="det-total" />
                <Indicator label="Enviados" value={s.enviado} tone="emerald" testid="det-enviados" />
                <Indicator label="Pendientes" value={(s.pendiente || 0) + (s.en_proceso || 0)} tone="amber" testid="det-pendientes" />
                <Indicator label="Errores" value={s.error} tone="red" testid="det-errores" />
              </div>
              <div className="text-xs text-slate-400 flex flex-wrap gap-x-6 gap-y-1">
                <span>Inicio: {camp.started_at ? new Date(camp.started_at).toLocaleString("es-MX") : "—"}</span>
                <span>Fin: {camp.finished_at ? new Date(camp.finished_at).toLocaleString("es-MX") : "—"}</span>
                {camp.scheduled_at && <span className="text-blue-300"><Clock className="w-3 h-3 inline mr-1" />Programada: {new Date(camp.scheduled_at).toLocaleString("es-MX")}</span>}
                {camp.simulated && <span className="text-amber-400 font-semibold">ENVÍO SIMULADO (Twilio pendiente)</span>}
              </div>

              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Destinatarios</h3>
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer" data-testid="toggle-only-errors">
                  <Checkbox checked={onlyErrors} onCheckedChange={() => setOnlyErrors((v) => !v)} className="border-slate-600 data-[state=checked]:bg-red-600" /> Solo errores
                </label>
              </div>
              <div className="border border-[#223048] rounded-lg overflow-hidden overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="border-[#223048] hover:bg-transparent">
                    <TableHead className="text-slate-400">Cliente</TableHead>
                    <TableHead className="text-slate-400">Empresa</TableHead>
                    <TableHead className="text-slate-400">Teléfono</TableHead>
                    <TableHead className="text-slate-400">Estado</TableHead>
                    <TableHead className="text-slate-400">Detalle</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(recs?.rows || []).map((r) => (
                      <TableRow key={r.id} className="border-[#223048]">
                        <TableCell className="text-slate-200">{r.partner_name}</TableCell>
                        <TableCell className="text-slate-400">{r.company_name || "—"}</TableCell>
                        <TableCell className="text-slate-300 tabular-nums">{r.phone_normalized}</TableCell>
                        <TableCell className={MSG_STATE[r.status] || "text-slate-400"}>{r.status}</TableCell>
                        <TableCell className="text-xs text-slate-500">{r.error_message || r.twilio_message_sid || "—"}</TableCell>
                      </TableRow>
                    ))}
                    {!(recs?.rows || []).length && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">Sin registros.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>

            <DialogFooter className="gap-2">
              {(camp.status === "borrador" || camp.status === "programada") && (
                <Button onClick={() => action.mutate("execute")} disabled={action.isPending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="execute-campania"><Play className="w-4 h-4 mr-1.5" />Ejecutar ahora</Button>
              )}
              {["borrador", "programada", "procesando"].includes(camp.status) && (
                <Button onClick={() => action.mutate("cancel")} disabled={action.isPending} variant="outline" className="border-red-600/40 text-red-300 hover:bg-red-600/10" data-testid="cancel-campania"><Ban className="w-4 h-4 mr-1.5" />Cancelar</Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
